import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Bike as BikeIcon, ChevronLeft, Loader2 } from "lucide-react";
import { api, bikeDescription, CATEGORY_LABELS, CLOSE_HOUR, euro, fmtDays, imageUri, OPEN_HOUR, SIZE_HEIGHTS, SIZES, SPEC_KEYS, specValue, toISODate } from "@/lib/noleggio";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";

const DAYS_OPTS = [1, 2, 3, 4, 5, 7];

export default function BikeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const cart = useCart();
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [nDays, setNDays] = useState(1);
  const [size, setSize] = useState(null);

  const bikeQ = useQuery({ queryKey: ["bike", id], queryFn: () => api(`/bikes/${id}`) });
  const quoteQ = useQuery({
    queryKey: ["quote", id, date, nDays],
    queryFn: () => api(`/bikes/${id}/quote`, { method: "POST", body: { bike_id: id, size: size ?? "M", mode: "daily", date, days: nDays, payment_method: "store" } }),
    enabled: !!bikeQ.data,
  });
  const avail = quoteQ.data?.available;
  useEffect(() => {
    if (size && avail && (avail[size] ?? 0) <= 0) setSize(null);
  }, [avail, size]);

  if (bikeQ.isLoading) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin" color="var(--brand-primary)" /></div>;
  const bike = bikeQ.data;
  if (!bike) return <div className="py-32 text-center text-[var(--muted)]">{t("bike_not_found")}</div>;

  const checkoutUrl = `/checkout?bike_id=${id}&size=${size}&date=${date}&days=${nDays}`;
  const book = () => nav(user ? checkoutUrl : `/accedi?next=${encodeURIComponent(checkoutUrl)}`);
  const addToCart = () => {
    cart.add({ bike_id: id, size, date, days: nDays });
    toast.success(t("cart_added", { name: bike.name, s: size }), { action: { label: t("cart_go"), onClick: () => nav("/checkout") } });
  };
  const specs = SPEC_KEYS.filter((k) => bike.specs?.[k]);
  const desc = bikeDescription(bike);

  return (
    <div className="max-w-6xl mx-auto md:px-8 md:pt-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-10 [&>*]:min-w-0">
        <div className="relative h-[340px] md:h-[520px] md:rounded-3xl overflow-hidden bg-[var(--surface-3)] md:sticky md:top-24">
          {bike.image_url ? <img src={imageUri(bike.image_url)} alt={bike.name} className="w-full h-full object-cover" /> : (
            <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><BikeIcon size={96} /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
          <button data-testid="bike-detail-back" onClick={() => nav(-1)} className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform">
            <ChevronLeft size={24} />
          </button>
          <div className="absolute left-6 right-6 bottom-5 text-white">
            <p className="text-xs font-bold uppercase tracking-wider">{CATEGORY_LABELS[bike.category]}</p>
            <h1 data-testid="bike-detail-name" className="text-3xl md:text-4xl font-extrabold tracking-tight">{bike.name}</h1>
            <p className="font-semibold opacity-90 mt-1">{euro(bike.price_day)}{t("per_day")}</p>
          </div>
        </div>

        <div className="px-5 md:px-0 pt-5 md:pt-0">
          {desc && <p data-testid="bike-description" className="text-[15px] leading-relaxed text-[#1c1c1e]">{desc}</p>}
          {specs.length > 0 && (
            <>
              <p className="mn-section">{t("specs")}</p>
              <div data-testid="bike-specs" className="bg-white rounded-2xl px-4 divide-y divide-[var(--border)]">
                {specs.map((k) => (
                  <div key={k} className="flex justify-between gap-4 py-3 text-sm">
                    <span className="text-[var(--muted)] shrink-0">{t(`spec_${k}`)}</span>
                    <span data-testid={`bike-spec-${k}`} className="font-semibold text-right">{specValue(bike.specs[k])}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <p className="mn-section">{t("from_day")}</p>
          <AvailabilityCalendar bikeId={id} size={size} value={date} onChange={setDate} />

          <p className="mn-section">{t("num_days")}</p>
          <div className="mn-hscroll">
            {DAYS_OPTS.map((d) => (
              <button key={d} data-testid={`days-${d}`} onClick={() => setNDays(d)} className={`mn-chip ${nDays === d ? "mn-chip-on" : ""}`}>{fmtDays(d)}</button>
            ))}
          </div>
          <p className="text-sm text-[var(--muted)] mt-2">{t("daily_hint", { open: OPEN_HOUR, close: CLOSE_HOUR })}</p>

          <div className="flex items-center justify-between">
            <p className="mn-section">{t("size_by_height")}</p>
            {quoteQ.isFetching && <Loader2 size={16} className="animate-spin" color="var(--brand-primary)" />}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {SIZES.map((s) => {
              const n = avail?.[s] ?? 0;
              const off = !avail || n <= 0;
              const on = size === s;
              return (
                <button key={s} data-testid={`size-option-${s}`} disabled={off} onClick={() => setSize(s)}
                  className={`rounded-xl border py-3 flex flex-col items-center gap-0.5 transition-colors ${on ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : off ? "bg-[var(--surface-2)] border-[var(--surface-2)] cursor-not-allowed" : "bg-white border-[var(--border)] hover:border-[var(--brand)]"}`}>
                  <span className={`text-xl font-extrabold ${off && !on ? "text-[var(--muted)]" : ""}`}>{s}</span>
                  <span className={`text-[10px] ${on ? "" : "text-[var(--muted)]"}`}>{SIZE_HEIGHTS[s]}</span>
                  <span data-testid={`size-available-${s}`} className={`text-[11px] font-bold mt-1 ${on ? "" : off ? "text-[var(--error)]" : "text-[var(--success)]"}`}>
                    {off ? t("sold_out") : t(n === 1 ? "available_one" : "available_many", { n })}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="sticky bottom-16 md:bottom-4 mt-8 mb-4 z-20 bg-white/90 backdrop-blur-xl rounded-2xl border border-[var(--border)] p-4 flex items-center gap-3 shadow-lg">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[var(--muted)]">{t("total")}</p>
              <p data-testid="bike-quote-total" className="text-2xl font-extrabold">{quoteQ.data ? euro(quoteQ.data.total) : "—"}</p>
            </div>
            <button data-testid="add-to-cart-button" className="mn-btn mn-btn-ghost !px-4" disabled={!size || !quoteQ.data} onClick={addToCart} title={t("cart_add")}>
              <ShoppingBag size={18} /><span className="hidden sm:inline">{t("cart_add")}</span>
            </button>
            <button data-testid="book-now-button" className="mn-btn flex-[1.3]" disabled={!size || !quoteQ.data} onClick={book}>
              {size ? t("book_now") : t("choose_size")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
